import { P2GBox, P2GGrid } from '@parcel2go/core-ui';
import DebouncedInput from '../../../DebouncedInput';
import { P2GButtonGroup, P2GButtonGroupItem } from '@parcel2go/core-ui';
import { P2GDropdown } from '@parcel2go/core-ui';
import { P2GSticky } from '@parcel2go/core-ui';
import { RoyalMailProvider } from '~/lib/consts/providers';
import ProviderName from '../../../../lib/helpers/ProviderName';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { FilterAction } from '../FilterAction';
import { QuoteTagType } from '../utils/QuoteTagType';
import { QuoteFilter } from '../utils/FilterQuotes';
import QuoteLayoutFilter from '../filter/QuoteFilter';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GContainer } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { sanitiseKey } from '~/lib/utils/translation';

interface props {
  categories: { Name: string; Id: string }[];
  isXs: boolean;
  multiAccount: boolean;
  filter: QuoteFilter;
  provider: string;
  setFilter: (s: QuoteFilter) => void;
  setSearch: (s: string) => void;
  setServiceType: (s: string) => void;
  serviceTypes: { value: string; text: string }[];
  productNames: { value: string; text: string }[];
  toggleFilterSubCategory: (x: QuoteTagType) => void;
  onAccountSwitch: () => void;
  setQuoteTagType: (x: QuoteTagType) => void;
  quoteTagType: QuoteTagType;
  includeAllServices: () => void;
  excludeAllServices: () => void;
  setMatchAll: (v: boolean) => void;
  setTags: (tags: string[]) => void;
}

export const QuoteLayoutTaskbar: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const hasCategories = props.categories.length > 1;
  return (
    <P2GSticky align="top" backgroundColor="transparent">
      <P2GBox pt={1} pb={1} sx={{ backgroundColor: 'white' }}>
        {props.isXs ? (
          <P2GGrid container alignItems="center" spacing={1}>
            {props.categories.length > 1 && (
              <P2GGrid item xs={12} sm={'auto'} flexShrink={1}>
                <P2GButtonGroup fullWidth>
                  {props.categories.map((c) => (
                    <P2GButtonGroupItem
                      key={c.Id}
                      selected={props.filter.SubCategory}
                      onClick={props.toggleFilterSubCategory}
                      value={c.Id}
                    >
                      {t(sanitiseKey(`TAB_${c.Name}`), { defaultValue: c.Name })}
                    </P2GButtonGroupItem>
                  ))}
                </P2GButtonGroup>
              </P2GGrid>
            )}
            <P2GGrid item xs={8}>
              <DebouncedInput
                id="Search"
                label={t('QUOTE_SEARCH_INPUT_LABEL', {
                  defaultValue: 'Search {{courier}} services...',
                  courier: ProviderName(props.provider)
                })}
                value={props.filter.Search}
                onChange={props.setSearch}
              />
            </P2GGrid>
            {props.serviceTypes.length > 1 && (
              <P2GGrid item xs={4}>
                <P2GDropdown
                  options={props.serviceTypes}
                  id="Type"
                  label={t('QUOTE_SERVICE_TYPE_INPUT_LABEL', { defaultValue: 'Service Type' })}
                  onChange={props.setServiceType}
                  value={props.filter.ServiceType}
                />
              </P2GGrid>
            )}
            {props.provider === RoyalMailProvider && (
              <>
                <P2GGrid item>
                  <QuoteLayoutFilter
                    isXs={props.isXs}
                    filter={props.filter}
                    productNames={props.productNames}
                    setFilter={props.setFilter}
                    setMatchAllTags={props.setMatchAll}
                    setTags={props.setTags}
                  />
                </P2GGrid>
                {SettingsCache.settings.Features.includes('CustomiseServices') && (
                  <P2GGrid item flexShrink={1}>
                    <FilterAction
                      setQuoteTagType={props.setQuoteTagType}
                      quoteTagType={props.quoteTagType}
                      includeAll={props.includeAllServices}
                      excludeAll={props.excludeAllServices}
                      setShowAll={() => props.setQuoteTagType('Default')}
                    />
                  </P2GGrid>
                )}
              </>
            )}
          </P2GGrid>
        ) : (
          <P2GContainer maxWidth="lg">
            <P2GGrid container alignItems="center" spacing={2} justify={hasCategories ? undefined : 'center'}>
              {hasCategories && (
                <>
                  <P2GGrid item flexShrink={1}>
                    <P2GButtonGroup>
                      {props.categories.map((c) => (
                        <P2GButtonGroupItem
                          key={c.Id}
                          selected={props.filter.SubCategory}
                          onClick={props.toggleFilterSubCategory}
                          value={c.Id}
                        >
                          {t(sanitiseKey(`TAB_${c.Name}`), { defaultValue: c.Name })}
                        </P2GButtonGroupItem>
                      ))}
                    </P2GButtonGroup>
                  </P2GGrid>
                  <P2GGrid item flexGrow={1} />
                </>
              )}
              <P2GGrid item>
                <DebouncedInput
                  id="Search"
                  label={t('QUOTE_SEARCH_INPUT_LABEL', {
                    defaultValue: 'Search {{courier}} services...',
                    courier: ProviderName(props.provider)
                  })}
                  value={props.filter.Search}
                  onChange={props.setSearch}
                />
              </P2GGrid>
              {props.serviceTypes.length > 1 && (
                <P2GGrid item flexShrink={1}>
                  <P2GDropdown
                    options={props.serviceTypes}
                    id="Type"
                    label={t('QUOTE_SERVICE_TYPE_INPUT_LABEL', { defaultValue: 'Service Type' })}
                    onChange={props.setServiceType}
                    value={props.filter.ServiceType}
                  />
                </P2GGrid>
              )}
              {props.provider === RoyalMailProvider && (
                <>
                  <P2GGrid item flexShrink={1}>
                    <QuoteLayoutFilter
                      isXs={props.isXs}
                      filter={props.filter}
                      productNames={props.productNames}
                      setFilter={props.setFilter}
                      setMatchAllTags={props.setMatchAll}
                      setTags={props.setTags}
                    />
                  </P2GGrid>
                  {SettingsCache.settings.Features.includes('CustomiseServices') && (
                    <P2GGrid item flexShrink={1}>
                      <FilterAction
                        setQuoteTagType={props.setQuoteTagType}
                        quoteTagType={props.quoteTagType}
                        includeAll={props.includeAllServices}
                        excludeAll={props.excludeAllServices}
                        setShowAll={() => props.setQuoteTagType('Default')}
                      />
                    </P2GGrid>
                  )}
                </>
              )}
              {props.multiAccount && props.onAccountSwitch && (
                <P2GGrid item flexShrink={1}>
                  <P2GButton variant="primary" onClick={props.onAccountSwitch}>
                    {t('SWITCH_ACCOUNT_BUTTON', { defaultValue: 'Switch Account' })}
                  </P2GButton>
                </P2GGrid>
              )}
            </P2GGrid>
          </P2GContainer>
        )}
      </P2GBox>
    </P2GSticky>
  );
};
